import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { RotateCcw } from 'lucide-react';
import { cn } from '@/utils/cn';
import { Button } from '@/components/atoms/Button';
import { Icon } from '@/components/atoms/Icon';
import drawerStyles from '@/components/molecules/Drawer/Drawer.module.scss';
import styles from './MistakesReviewDrawer.module.scss';

export interface MistakesReviewDrawerProps {
  open: boolean;
  mistakesCount: number;
  onReviewMistakes: () => void;
  onSubmitAnyway: () => void;
  disableSubmit?: boolean;
}

/**
 * Pre-submit panel: mismo layout interno que el Drawer (header / title / description / footer),
 * pero sin Radix Dialog — no se cierra con X, overlay, swipe ni Escape; solo con las acciones.
 */
export function MistakesReviewDrawer({
  open,
  mistakesCount,
  onReviewMistakes,
  onSubmitAnyway,
  disableSubmit = false,
}: MistakesReviewDrawerProps) {
  const { t, i18n } = useTranslation();
  const description = t('mistakesReview.desc', { count: mistakesCount });

  if (!open || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div className={styles.root}>
      <div className={styles.backdrop} aria-hidden />
      <div
        className={cn(drawerStyles.drawer__content, styles.sheet)}
        data-side="bottom"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mistakes-review-title"
        lang={i18n.language === 'en' ? 'en' : 'es'}
      >
        <div className={drawerStyles.drawer__header}>
          <div className={styles.mistakesReviewDrawer__icon} aria-hidden>
            <Icon icon={RotateCcw} size="md" />
          </div>
          <h2 id="mistakes-review-title" className={drawerStyles.drawer__title}>
            {t('mistakesReview.title')}
          </h2>
          <p className={drawerStyles.drawer__description}>{description}</p>
        </div>
        <div className={drawerStyles.drawer__footer}>
          <Button variant="ghost" onClick={onSubmitAnyway} disabled={disableSubmit}>
            {t('mistakesReview.submitAnyway')}
          </Button>
          <Button variant="primary" onClick={onReviewMistakes}>
            {t('mistakesReview.reviewMistakes')}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}

MistakesReviewDrawer.displayName = 'MistakesReviewDrawer';
